import React, { FC, useState, useEffect, MouseEvent, useMemo } from 'react';
import { Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import {
  ChevronLeft,
  WebAsset,
  Logout,
  Language,
  MoreVert,
  NotificationImportant,
  Lightbulb,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import JwtValidator from '../../utils/JwtValidator';
import { useNavigate } from 'react-router-dom';
import { cn } from '@thingslog/ui-components';
import { useViewport, useCurrentCompanyId } from '../../hooks';
import { companyInfoQueryClient } from '../../utils/ReactQueryClients';

interface PageHeaderProps {
  className?: string;
  title?: string;
  logo?: Blob | null;
  options?: PageHeaderControls;
  mode?: 'default' | 'sticky';
}

export interface PageHeaderControls {
  showBackButton?: boolean;
  showLogoutButton?: boolean;
  showLanguageButton?: boolean;
  showRedirectThingslogButton?: boolean;
  showAlarmsButton?: boolean;
  showTaskSchedulerButton?: boolean;
  currDeviceNumber?: string;
}

export const PageHeader: FC<PageHeaderProps> = ({
  options,
  title,
  className,
  logo: logoBlob,
  mode = 'default',
}: PageHeaderProps) => {
  const isMobile = useViewport('sm');
  const [logo, setLogo] = useState<string | null>(null);
  const {
    showLanguageButton,
    showLogoutButton,
    showRedirectThingslogButton,
    showBackButton,
    showAlarmsButton,
    showTaskSchedulerButton,
  } = options || {};
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const jwtValidator = new JwtValidator();
  const [desktopLanguageAnchorEl, setDesktopLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const desktopLanguageSelectorOpen = Boolean(desktopLanguageAnchorEl);
  const mobileMenuOpen = Boolean(mobileMenuAnchorEl);
  const languageOptions: Record<string, string> = {
    Български: 'bg',
    English: 'en',
    Deutsch: 'de',
    Português: 'ptBr',
    Français: 'fr',
  };

  const companyId = useCurrentCompanyId();
  const { useCompanyInfo } = companyInfoQueryClient;
  const companyInfo = useCompanyInfo(companyId ?? 0, {
    enabled: companyId !== undefined,
    refetchOnWindowFocus: false,
  });

  const resolvedLogoBlob = useMemo(() => {
    if (logoBlob !== undefined) {
      return logoBlob;
    }
    if (companyInfo.data?.logo) {
      const byteCharacters = atob(companyInfo.data.logo);
      const byteNumbers = Array.from(byteCharacters).map((char: string) => char.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray]);
    }
    return null;
  }, [logoBlob, companyInfo.data]);

  useEffect(() => {
    if (resolvedLogoBlob !== null) {
      const logo = URL.createObjectURL(resolvedLogoBlob);
      setLogo(logo);

      return () => {
        URL.revokeObjectURL(logo);
      };
    }
    setLogo(null);
  }, [resolvedLogoBlob]);

  // Left Section
  const isBackButtonVisible = useMemo(() => {
    return showBackButton;
  }, [isMobile, showBackButton]);

  const isAlarmsButtonVisible = useMemo(() => {
    return !isMobile && showAlarmsButton;
  }, [isMobile, showAlarmsButton]);

  const isTaskSchedulerButtonVisible = useMemo(() => {
    return !isMobile && showTaskSchedulerButton;
  }, [isMobile, showTaskSchedulerButton]);

  const isDesktopLeftSectionVisible = useMemo(() => {
    return (
      (isBackButtonVisible || isAlarmsButtonVisible || isTaskSchedulerButtonVisible) && !isMobile
    );
  }, [isBackButtonVisible, isAlarmsButtonVisible, isTaskSchedulerButtonVisible, isMobile]);

  const isMobileLeftSectionVisible = useMemo(() => {
    return isBackButtonVisible && isMobile;
  }, [isBackButtonVisible, isMobile]);

  // Middle Section
  const isTitleVisible = useMemo(() => {
    return title !== undefined && title !== null && title !== '';
  }, [title]);

  const isLogoVisible = useMemo(() => {
    return logo !== null && logo !== undefined;
  }, [logo]);

  // Right Section
  const isLanguageButtonVisible = useMemo(() => {
    return showLanguageButton;
  }, [showLanguageButton]);

  const isRedirectThingslogButtonVisible = useMemo(() => {
    return showRedirectThingslogButton;
  }, [showRedirectThingslogButton]);

  const isLogoutButtonVisible = useMemo(() => {
    return showLogoutButton;
  }, [showLogoutButton]);

  const isDesktopRightSectionVisible = useMemo(() => {
    return (
      (isLanguageButtonVisible || isRedirectThingslogButtonVisible || isLogoutButtonVisible) &&
      !isMobile
    );
  }, [isLanguageButtonVisible, isRedirectThingslogButtonVisible, isLogoutButtonVisible, isMobile]);

  const isMobileRightSectionVisible = useMemo(() => {
    return (
      (isLanguageButtonVisible || isRedirectThingslogButtonVisible || isLogoutButtonVisible) &&
      isMobile
    );
  }, [isLanguageButtonVisible, isRedirectThingslogButtonVisible, isLogoutButtonVisible, isMobile]);

  const handleMobileMoreInformationClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const removeAllAnchorElements = (): void => {
    setMobileMenuAnchorEl(null);
    setDesktopLanguageAnchorEl(null);
  };

  const handleLanguageClick = (language: string): void => {
    i18n.changeLanguage(language);
    removeAllAnchorElements();
  };

  const handleRedirectThingslogClick = (): void => {
    const location = window.location;
    window.location.href = `${location.protocol}//${location.host}/app`;
    removeAllAnchorElements();
  };

  const handleLogoutClick = (): void => {
    jwtValidator.logout();
    removeAllAnchorElements();
  };

  const handleBackButtonClick = (): void => {
    window.history.back();
  };

  const handleAlarmsButtonClick = (): void => {
    if (options?.currDeviceNumber) {
      navigate(`/alarms?devNum=${options.currDeviceNumber}`);
    } else {
      navigate('/alarms');
    }
  };

  const handleTaskSchedulerButtonClick = (): void => {
    navigate('/taskScheduler');
  };

  const handleDesktopLanguageButtonClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setDesktopLanguageAnchorEl(event.currentTarget);
  };

  return (
    <section
      className={cn(
        'flex justify-between items-center',
        mode === 'sticky' && 'sticky top-0 z-30 bg-gray-100/95 backdrop-blur',
        mode === 'sticky' && 'supports-[backdrop-filter]:bg-gray-100/80',
        'min-h-[3.5rem] px-3 py-2 sm:min-h-[4rem] sm:px-4 sm:py-2.5 lg:px-6',
        className
      )}
    >
      {isMobileLeftSectionVisible && (
        <div>
          {isBackButtonVisible && (
            <IconButton size="small" onClick={handleBackButtonClick}>
              <ChevronLeft fontSize="small" />
            </IconButton>
          )}
        </div>
      )}
      {isLogoVisible && <img src={logo!} className="h-9 max-w-[12rem] object-contain sm:h-12" />}
      <div className="flex min-w-0 flex-1 items-center justify-center gap-1 text-center">
        {isTitleVisible && (
          <Typography
            variant={isMobile ? 'subtitle1' : 'h6'}
            fontSize={isMobile ? '1.125rem' : '1.375rem'}
            fontWeight="bold"
            className="truncate px-2"
          >
            {title}
          </Typography>
        )}
      </div>
      {isDesktopRightSectionVisible && (
        <div className="flex min-w-0 items-center gap-2">
          {isLanguageButtonVisible && (
            <div>
              <Button
                size="medium"
                className="min-w-0 whitespace-nowrap"
                startIcon={<Language />}
                onClick={handleDesktopLanguageButtonClick}
              >
                {t('language_button')}
              </Button>
              <Menu
                anchorEl={desktopLanguageAnchorEl}
                open={desktopLanguageSelectorOpen}
                onClose={removeAllAnchorElements}
              >
                {Object.entries(languageOptions).map(([key, value]: [string, string]) => (
                  <MenuItem
                    className="text-sm"
                    key={key}
                    onClick={(): void => handleLanguageClick(value)}
                  >
                    <Language fontSize="small" />
                    {key}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          )}
          {isRedirectThingslogButtonVisible && (
            <Button
              size="medium"
              className="min-w-0 whitespace-nowrap"
              startIcon={<WebAsset />}
              onClick={handleRedirectThingslogClick}
            >
              ThingsLog
            </Button>
          )}
          {isLogoutButtonVisible && (
            <Button
              size="medium"
              className="min-w-0 whitespace-nowrap"
              startIcon={<Logout />}
              onClick={handleLogoutClick}
            >
              {t('logout_button')}
            </Button>
          )}
        </div>
      )}
      {isMobileRightSectionVisible && (
        <div>
          <IconButton size="small" onClick={handleMobileMoreInformationClick}>
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={mobileMenuAnchorEl}
            open={mobileMenuOpen}
            onClose={removeAllAnchorElements}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {showLanguageButton === true &&
              Object.entries(languageOptions).map(([key, value]: [string, string]) => (
                <MenuItem
                  sx={{ paddingX: 1, paddingY: 0, gap: 1 }}
                  key={key}
                  onClick={(): void => handleLanguageClick(value)}
                >
                  <Language fontSize="small" />
                  {key}
                </MenuItem>
              ))}
            {showRedirectThingslogButton === true && (
              <MenuItem
                sx={{ paddingX: 1, paddingY: 0, gap: 1 }}
                onClick={handleRedirectThingslogClick}
              >
                <WebAsset fontSize="small" />
                ThingsLog
              </MenuItem>
            )}
            {showLogoutButton === true && (
              <MenuItem sx={{ paddingX: 1, paddingY: 0, gap: 1 }} onClick={handleLogoutClick}>
                <Logout fontSize="small" />
                {t('logout_button')}
              </MenuItem>
            )}
          </Menu>
        </div>
      )}
    </section>
  );
};
